// components/ui/ProgressBar.tsx
// UI Phase 1 primitive: THE progress bar. Replaces (over Phase 2) the four
// parallel implementations in the degree audit. Segments share the global
// status color contract; the fill animates with the slow/soft motion tokens.

import type { HTMLAttributes } from 'react';
import { cn } from './cn';
import type { BadgeStatus } from './Badge';

export type ProgressStatus = Exclude<BadgeStatus, 'neutral'>;

export interface ProgressSegment {
  /** Absolute value of this segment (same unit as max) */
  value: number;
  status: ProgressStatus;
}

export interface ProgressBarProps extends HTMLAttributes<HTMLDivElement> {
  segments: ProgressSegment[];
  /** Total the segments are measured against (default 100) */
  max?: number;
  size?: 'sm' | 'md';
  /** Accessible label, e.g. "Computer Science Major progress" */
  label: string;
}

const SEGMENT_CLASSES: Record<ProgressStatus, string> = {
  completed: 'bg-status-completed',
  progress: 'bg-status-progress',
  planned: 'bg-status-planned',
  suggested: 'bg-status-suggested',
  warning: 'bg-status-warning',
  critical: 'bg-status-critical',
};

const SIZE_CLASSES = { sm: 'h-1.5', md: 'h-2.5' } as const;

const ProgressBar = ({
  segments,
  max = 100,
  size = 'sm',
  label,
  className,
  ...rest
}: ProgressBarProps) => {
  const safeMax = max > 0 ? max : 100;
  const total = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0);

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={safeMax}
      aria-valuenow={Math.min(total, safeMax)}
      className={cn(
        'w-full flex overflow-hidden rounded-full bg-surface-sunken',
        SIZE_CLASSES[size],
        className
      )}
      {...rest}
    >
      {segments
        .filter((s) => s.value > 0)
        .map((segment, idx) => (
          <div
            key={`${segment.status}-${idx}`}
            data-status={segment.status}
            className={cn(
              'h-full transition-all duration-slow ease-out-soft first:rounded-l-full last:rounded-r-full',
              SEGMENT_CLASSES[segment.status]
            )}
            style={{ width: `${Math.min(100, (segment.value / safeMax) * 100)}%` }}
          />
        ))}
    </div>
  );
};

export default ProgressBar;
